import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
    Address,
    Quote,
    QuoteItemInput,
    QuoteLineItem,
    QuoteShippingAddressOption,
    QuoteShippingMethod,
} from '../types/myaccount';
import api from '../services/api';
import MyAccountPageShell from './MyAccountPageShell';
import QuoteShippingPanel from './QuoteShippingPanel';
import { LoadingBar, TableSkeleton } from './MyAccountLoadingStates';
import { getQuoteDisplayId } from './quoteDisplay';

interface QuoteProduct {
    rowId: string;
    lineItemId: number;
    productId: number;
    variationId: number;
    name: string;
    sku: string;
    quote_qty: number;
    quote_price: number;
    quote_discount: number;
}

const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;

interface EditQuoteProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const EditQuote: React.FC<EditQuoteProps> = ({ wekaPagePermissions = {} }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [quote, setQuote] = useState<Quote | null>(null);
    const [quoteTitle, setQuoteTitle] = useState('');
    const [quoteNotes, setQuoteNotes] = useState('');
    const [billingAddress, setBillingAddress] = useState<Address | null>(null);
    const [billingAddresses, setBillingAddresses] = useState<QuoteShippingAddressOption[]>([]);
    const [quoteItems, setQuoteItems] = useState<QuoteProduct[]>([]);
    const [shippingMethods, setShippingMethods] = useState<QuoteShippingMethod[]>([]);
    const [selectedShippingRateId, setSelectedShippingRateId] = useState('');
    const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(0);
    const [requiresShipping, setRequiresShipping] = useState(false);
    const [shippingAddress, setShippingAddress] = useState<Address | null>(null);
    const [shippingLoading, setShippingLoading] = useState(false);
    const [shippingError, setShippingError] = useState<string | null>(null);
    const [shippingMessage, setShippingMessage] = useState<string | null>(null);
    const [shippingAddresses, setShippingAddresses] = useState<QuoteShippingAddressOption[]>([]);
    const [selectedShippingAddressId, setSelectedShippingAddressId] = useState(0);
    const [useBillingAsShipping, setUseBillingAsShipping] = useState(true);
    const [shippingContextLoading, setShippingContextLoading] = useState(true);
    const [shippingContextError, setShippingContextError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const canEditQuote = wekaPagePermissions.edit_quote !== false;

    const buildQuoteItemPayload = (): QuoteItemInput[] => (
        quoteItems.map((item) => ({
            ...(item.lineItemId > 0 ? { line_item_id: item.lineItemId } : {}),
            product_id: item.productId,
            quantity: item.quote_qty,
            price: item.quote_price,
            discount: item.quote_discount,
            ...(item.variationId > 0 ? { variation_id: item.variationId } : {}),
        }))
    );

    useEffect(() => {
        if (!id) {
            return;
        }

        if (!canEditQuote) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const loadQuoteContext = async () => {
            try {
                setLoading(true);
                setError(null);
                setShippingContextLoading(true);
                setShippingContextError(null);

                const quoteId = parseInt(id, 10);
                const [quoteData, fulfillmentOptions] = await Promise.all([
                    api.getQuote(quoteId),
                    api.getQuoteFulfillmentOptions(),
                ]);

                if (cancelled) {
                    return;
                }

                const loadedShippingAddresses = Array.isArray(fulfillmentOptions.shipping_addresses)
                    ? fulfillmentOptions.shipping_addresses
                    : [];
                const loadedBillingAddresses = Array.isArray(fulfillmentOptions.billing_addresses)
                    ? fulfillmentOptions.billing_addresses
                    : [];
                const defaultBillingAddressId = Number(fulfillmentOptions.default_billing_address_id || 0);
                const defaultShippingAddressId = Number(fulfillmentOptions.default_shipping_address_id || 0);
                const quoteBillingAddressId = Number(quoteData.billing_address_id || 0);
                const quoteShippingAddressId = Number(quoteData.shipping_address_id || 0);
                const resolvedBillingAddressId = loadedBillingAddresses.some((address) => address.id === quoteBillingAddressId)
                    ? quoteBillingAddressId
                    : (loadedBillingAddresses.some((address) => address.id === defaultBillingAddressId)
                        ? defaultBillingAddressId
                        : (loadedBillingAddresses[0]?.id ?? 0));
                const resolvedShippingAddressId = loadedShippingAddresses.some((address) => address.id === quoteShippingAddressId)
                    ? quoteShippingAddressId
                    : (loadedShippingAddresses.some((address) => address.id === defaultShippingAddressId)
                        ? defaultShippingAddressId
                        : (loadedShippingAddresses[0]?.id ?? 0));
                const resolvedUseBillingAsShipping = typeof quoteData.use_billing_as_shipping === 'boolean'
                    ? quoteData.use_billing_as_shipping
                    : (quoteShippingAddressId > 0
                        ? quoteShippingAddressId === resolvedBillingAddressId
                        : resolvedShippingAddressId === resolvedBillingAddressId);

                setQuote(quoteData);
                setQuoteTitle(quoteData.title);
                setQuoteNotes(quoteData.notes || '');
                setSelectedShippingRateId(String(quoteData.selected_shipping_rate_id || '').trim());
                setBillingAddresses(loadedBillingAddresses);
                setSelectedBillingAddressId(resolvedBillingAddressId);
                setUseBillingAsShipping(resolvedUseBillingAsShipping);
                setBillingAddress(
                    loadedBillingAddresses.find((address) => address.id === resolvedBillingAddressId)
                    || (quoteData.billing && typeof quoteData.billing === 'object' ? (quoteData.billing as Address) : null)
                );
                setShippingAddresses(loadedShippingAddresses);
                setSelectedShippingAddressId(resolvedShippingAddressId);
                setShippingAddress(
                    loadedShippingAddresses.find((address) => address.id === resolvedShippingAddressId)
                    || (quoteData.shipping && typeof quoteData.shipping === 'object' ? (quoteData.shipping as Address) : null)
                );

                if (Array.isArray(quoteData.line_items) && quoteData.line_items.length > 0) {
                    const items: QuoteProduct[] = quoteData.line_items.map((item: QuoteLineItem, index: number) => {
                        const lineItemId = Number(item.id || 0);
                        return {
                            rowId: lineItemId > 0
                                ? `line-item-${lineItemId}`
                                : `quote-item-${Number(item.variation_id || item.product_id || 0)}-${index}`,
                            lineItemId,
                            productId: Number(item.product_id),
                            variationId: Number(item.variation_id || 0),
                            name: item.product_name,
                            sku: item.sku,
                            quote_qty: Number(item.quantity || 0),
                            quote_price: parseFloat(String(item.unit_price || '0')),
                            quote_discount: parseFloat(String(item.discount || '0')),
                        };
                    });
                    setQuoteItems(items);
                } else {
                    setQuoteItems([]);
                }
            } catch (loadError) {
                if (!cancelled) {
                    console.error('Error loading quote:', loadError);
                    const message = loadError instanceof Error ? loadError.message : 'An error occurred';
                    setError(message);
                    setShippingContextError(message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setShippingContextLoading(false);
                }
            }
        };

        void loadQuoteContext();

        return () => {
            cancelled = true;
        };
    }, [id, canEditQuote]);

    useEffect(() => {
        const selectedAddress = billingAddresses.find((address) => address.id === selectedBillingAddressId) || null;
        setBillingAddress((currentAddress) => selectedAddress || currentAddress);
    }, [billingAddresses, selectedBillingAddressId]);

    useEffect(() => {
        const selectedAddress = shippingAddresses.find((address) => address.id === selectedShippingAddressId) || null;
        setShippingAddress((currentAddress) => selectedAddress || currentAddress);
    }, [shippingAddresses, selectedShippingAddressId]);

    const effectiveShippingAddressId = useBillingAsShipping ? 0 : selectedShippingAddressId;

    useEffect(() => {
        let cancelled = false;

        const loadShippingOptions = async () => {
            if (!quote) {
                setShippingMethods([]);
                setRequiresShipping(false);
                setShippingLoading(false);
                setShippingError(null);
                setShippingMessage(null);
                setSelectedShippingRateId('');
                return;
            }

            if (quoteItems.length === 0) {
                setShippingMethods([]);
                setRequiresShipping(false);
                setShippingLoading(false);
                setShippingError(null);
                setShippingMessage(null);
                setSelectedShippingRateId('');
                return;
            }

            try {
                setShippingLoading(true);
                setShippingError(null);

                const response = await api.getQuoteShippingOptions(buildQuoteItemPayload(), {
                    billingAddressId: selectedBillingAddressId,
                    quoteId: quote.id,
                    shippingAddressId: effectiveShippingAddressId,
                    useBillingAsShipping,
                });
                if (cancelled) {
                    return;
                }

                const methods = Array.isArray(response.methods) ? response.methods : [];
                setRequiresShipping(Boolean(response.requires_shipping));
                setShippingMethods(methods);
                setBillingAddress(
                    billingAddresses.find((address) => address.id === Number(response.billing_address_id || selectedBillingAddressId))
                    || response.billing_address
                    || billingAddresses.find((address) => address.id === selectedBillingAddressId)
                    || null
                );
                setShippingAddress(
                    shippingAddresses.find((address) => address.id === Number(response.shipping_address_id || effectiveShippingAddressId))
                    || response.shipping_address
                    || shippingAddresses.find((address) => address.id === effectiveShippingAddressId)
                    || null
                );

                setShippingError(null);
                setShippingMessage(response.message || null);

                setSelectedShippingRateId((currentRateId) => (
                    methods.some((method) => method.rate_id === currentRateId)
                        ? currentRateId
                        : (methods[0]?.rate_id || '')
                ));
            } catch (loadError) {
                if (!cancelled) {
                    console.error('Error loading quote shipping methods:', loadError);
                    setShippingMethods([]);
                    setSelectedShippingRateId('');
                    setRequiresShipping(false);
                    setShippingMessage(null);
                    setShippingError(loadError instanceof Error ? loadError.message : 'Failed to load shipping methods.');
                }
            } finally {
                if (!cancelled) {
                    setShippingLoading(false);
                }
            }
        };

        void loadShippingOptions();

        return () => {
            cancelled = true;
        };
    }, [billingAddresses, effectiveShippingAddressId, quote, quoteItems, selectedBillingAddressId, shippingAddresses, useBillingAsShipping]);

    const updateQuantity = (rowId: string, quantity: number) => {
        setQuoteItems((currentItems) => currentItems.map((item) => (
            item.rowId === rowId ? { ...item, quote_qty: Math.max(0, quantity) } : item
        )));
    };

    const removeFromQuote = (rowId: string) => {
        setQuoteItems((currentItems) => currentItems.filter((item) => item.rowId !== rowId));
    };

    const calculateLineTotal = (item: QuoteProduct) => (
        (item.quote_price * item.quote_qty) - item.quote_discount
    );

    const selectedShippingMethod = shippingMethods.find((method) => method.rate_id === selectedShippingRateId) || null;

    const calculateTotals = () => {
        const subtotal = quoteItems.reduce((sum, item) => sum + (item.quote_price * item.quote_qty), 0);
        const discount = quoteItems.reduce((sum, item) => sum + item.quote_discount, 0);
        const shipping = selectedShippingMethod
            ? parseFloat(selectedShippingMethod.total || '0')
            : parseFloat(String(quote?.shipping_total || '0'));
        const total = subtotal - discount + shipping;

        return { subtotal, discount, shipping, total };
    };

    const handleUpdateQuote = async () => {
        if (!canEditQuote) {
            alert('Editing quotes is disabled for this page.');
            return;
        }

        if (!quoteTitle.trim()) {
            alert('Please enter a quote title.');
            return;
        }

        if (quoteItems.length === 0) {
            alert('Please add at least one product to the quote.');
            return;
        }

        if (!quote) {
            return;
        }

        if (shippingContextLoading) {
            alert('Shipping settings are still loading. Please wait a moment and try again.');
            return;
        }

        if (shippingContextError) {
            alert(shippingContextError);
            return;
        }

        if (shippingLoading) {
            alert('Shipping methods are still loading. Please wait a moment and try again.');
            return;
        }

        if (shippingError) {
            alert(shippingError);
            return;
        }

        if (requiresShipping && selectedShippingRateId === '') {
            alert('Please select a shipping method for this quote.');
            return;
        }

        try {
            setSaving(true);

            const response = await api.updateQuote(quote.id, {
                title: quoteTitle.trim(),
                notes: quoteNotes,
                items: buildQuoteItemPayload(),
                fulfillment_type: 'shipping',
                billing_address_id: selectedBillingAddressId,
                shipping_address_id: effectiveShippingAddressId,
                use_billing_as_shipping: useBillingAsShipping,
                shipping_rate_id: selectedShippingRateId,
            });

            if (!response?.success) {
                throw new Error(response?.error || 'Failed to update quote');
            }

            navigate(`/quotes/${quote.id}`);
        } catch (saveError) {
            console.error('Error updating quote:', saveError);
            alert(saveError instanceof Error ? saveError.message : 'Failed to update quote');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MyAccountPageShell
                className="edit-quote"
                title="Edit Quote"
                subtitle="Update quote details, shipping, and current line items."
            >
                <div className="mb-4">
                    <LoadingBar height="20px" width="18%" style={{ marginBottom: '16px' }} />
                    <div className="row g-3">
                        <div className="col-md-6"><LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} /></div>
                        <div className="col-md-12"><div className="skeleton" style={{ height: '96px', borderRadius: '8px' }} /></div>
                    </div>
                </div>
                <div className="mb-4">
                    <LoadingBar height="20px" width="22%" style={{ marginBottom: '8px' }} />
                    <LoadingBar height="14px" width="44%" style={{ marginBottom: '16px' }} />
                    <TableSkeleton columns={6} rows={5} />
                </div>
                <div className="mb-4">
                    <div className="skeleton" style={{ height: '260px', borderRadius: '10px' }} />
                </div>
                <div className="d-flex justify-content-between">
                    <LoadingBar height="38px" width="120px" style={{ borderRadius: '8px' }} />
                    <LoadingBar height="38px" width="140px" style={{ borderRadius: '8px' }} />
                </div>
            </MyAccountPageShell>
        );
    }

    if (!canEditQuote) {
        return (
            <MyAccountPageShell
                title="Edit Quote"
                subtitle="Update quote details, shipping, and current line items."
            >
                <div className="alert alert-info mb-3" role="status">
                    Editing quotes is disabled for this page.
                </div>
                <button onClick={() => navigate(id ? `/quotes/${id}` : '/quotes')} className="button">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Quote
                </button>
            </MyAccountPageShell>
        );
    }

    if (error || !quote) {
        return (
            <MyAccountPageShell
                title="Edit Quote"
                subtitle="Update quote details, shipping, and current line items."
            >
                <div className="alert alert-danger mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error || 'Quote not found'}
                </div>
                <button onClick={() => navigate('/quotes')} className="button">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Quotes
                </button>
            </MyAccountPageShell>
        );
    }

    const isQuoteConverted = quote.conversion_state === 'converted' || quote.status === 'converted';
    const quoteDisplayId = getQuoteDisplayId(quote);

    if (
        quote.status === 'completed'
        || quote.status === 'quote-completed'
        || quote.status === 'rejected'
        || quote.has_active_checkout_order
        || isQuoteConverted
    ) {
        return (
            <MyAccountPageShell
                title={`Edit Quote: ${quoteDisplayId}`}
                subtitle="Update quote details, shipping, and current line items."
            >
                <div className="alert alert-warning mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {quote.has_active_checkout_order
                        ? 'This quote cannot be edited while a linked checkout order is awaiting payment.'
                        : isQuoteConverted
                            ? 'This quote cannot be edited because it has been converted to a linked order.'
                            : `This quote cannot be edited because it is ${(quote.status === 'completed' || quote.status === 'quote-completed') ? 'completed' : 'rejected'}.`}
                </div>
                <button onClick={() => navigate(`/quotes/${quote.id}`)} className="button">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Quote
                </button>
            </MyAccountPageShell>
        );
    }

    const totals = calculateTotals();
    const saveDisabled = saving
        || shippingContextLoading
        || quoteItems.length === 0
        || !quoteTitle.trim()
        || Boolean(shippingContextError)
        || shippingLoading
        || Boolean(shippingError)
        || (requiresShipping && selectedShippingRateId === '');

    return (
        <MyAccountPageShell
            className="edit-quote"
            title={`Edit Quote: ${quoteDisplayId}`}
            subtitle="Update quote details, shipping, and current line items."
        >
            <div className="mb-4">
                <h6 className="mb-3">Quote Details</h6>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label">Quote Title *</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., Warehouse Restock Quote"
                            value={quoteTitle}
                            onChange={(event) => setQuoteTitle(event.target.value)}
                        />
                    </div>
                    <div className="col-md-12">
                        <label className="form-label">Notes (Optional)</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Add any special requests or notes..."
                            value={quoteNotes}
                            onChange={(event) => setQuoteNotes(event.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <h6 className="mb-3">Quote Items ({quoteItems.length})</h6>
                <p className="text-muted small mb-3">
                    Unit price and discount are locked to the values captured when each item was added to the quote.
                </p>

                {quoteItems.length === 0 ? (
                    <div className="alert alert-info mb-0">
                        No products are currently attached to this quote.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '35%' }}>Product</th>
                                    <th style={{ width: '15%' }}>Qty</th>
                                    <th style={{ width: '15%' }}>Unit Price</th>
                                    <th style={{ width: '15%' }}>Discount</th>
                                    <th style={{ width: '15%' }} className="text-end">Total</th>
                                    <th style={{ width: '5%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {quoteItems.map((item) => (
                                    <tr key={item.rowId}>
                                        <td>
                                            <strong>{item.name}</strong>
                                            <br />
                                            <small className="text-muted">SKU: {item.sku || 'N/A'}</small>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                min="0"
                                                step="1"
                                                value={item.quote_qty}
                                                onChange={(event) => updateQuantity(item.rowId, parseFloat(event.target.value) || 0)}
                                            />
                                        </td>
                                        <td>
                                            <span className="fw-semibold">{formatCurrency(item.quote_price)}</span>
                                        </td>
                                        <td>
                                            <span className={item.quote_discount > 0 ? 'fw-semibold text-success' : 'text-muted'}>
                                                {item.quote_discount > 0 ? `-${formatCurrency(item.quote_discount)}` : formatCurrency(0)}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <strong>{formatCurrency(calculateLineTotal(item))}</strong>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => removeFromQuote(item.rowId)}
                                                title="Remove"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} className="text-end"><strong>Subtotal:</strong></td>
                                    <td className="text-end"><strong>{formatCurrency(totals.subtotal)}</strong></td>
                                    <td></td>
                                </tr>
                                {totals.discount > 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-end"><strong>Discount:</strong></td>
                                        <td className="text-end"><strong className="text-success">-{formatCurrency(totals.discount)}</strong></td>
                                        <td></td>
                                    </tr>
                                )}
                                {(requiresShipping || selectedShippingMethod || parseFloat(String(quote.shipping_total || '0')) > 0) && (
                                    <tr>
                                        <td colSpan={4} className="text-end"><strong>Shipping:</strong></td>
                                        <td className="text-end">
                                            <strong>
                                                {selectedShippingMethod || parseFloat(String(quote.shipping_total || '0')) > 0
                                                    ? formatCurrency(totals.shipping)
                                                    : 'Select method'}
                                            </strong>
                                        </td>
                                        <td></td>
                                    </tr>
                                )}
                                <tr>
                                    <td colSpan={4} className="text-end"><strong>Total:</strong></td>
                                    <td className="text-end"><strong className="text-primary">{formatCurrency(totals.total)}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            <QuoteShippingPanel
                billingAddress={billingAddress}
                billingAddresses={billingAddresses}
                emptyMethodsMessage={shippingMessage}
                hasItems={quoteItems.length > 0}
                loading={shippingLoading || shippingContextLoading}
                error={shippingContextError || shippingError}
                requiresShipping={requiresShipping}
                onBillingAddressChange={setSelectedBillingAddressId}
                shippingAddress={shippingAddress}
                shippingAddresses={shippingAddresses}
                selectedBillingAddressId={selectedBillingAddressId}
                selectedShippingAddressId={selectedShippingAddressId}
                shippingMethods={shippingMethods}
                selectedShippingRateId={selectedShippingRateId}
                onShippingAddressChange={setSelectedShippingAddressId}
                onSelectRate={setSelectedShippingRateId}
                onUseBillingAsShippingChange={setUseBillingAsShipping}
                useBillingAsShipping={useBillingAsShipping}
                disabled={saving}
            />

            <div className="d-flex justify-content-between">
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                >
                    <i className="fas fa-times me-2"></i>
                    Cancel
                </button>
                <button
                    className="btn btn-success"
                    onClick={handleUpdateQuote}
                    disabled={saveDisabled}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save me-2"></i>
                            Update Quote
                        </>
                    )}
                </button>
            </div>
        </MyAccountPageShell>
    );
};

export default EditQuote;
